import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { ClockIcon, MapPinIcon, ArrowRightIcon, MonitorIcon, CalendarIcon, GlobeIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { usePlatform } from '../../context/PlatformContext';
import SaveEventButton from './SaveEventButton';
export interface EventCardProps {
  title: string;
  date: string;
  time: string;
  type: string;
  description: string;
  image: string;
  location?: string;
  link?: string;
  eventId?: number;
  isSaved?: boolean;
  onToggleSave?: (id: number) => void;
  price?: string;
  children?: React.ReactNode;
  status?: {
    label: string;
    classes: string;
  };
  isVirtual?: boolean;
  dateRange?: string;
  ctaLabel?: string;
  ctaDisabled?: boolean;
  country?: string;
}
const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  const target = e.currentTarget;
  const fallback = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="600" viewBox="0 0 400 600"><defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:#e0e7ff"/><stop offset="50%" style="stop-color:#c7d2fe"/><stop offset="100%" style="stop-color:#a5b4fc"/></linearGradient></defs><rect width="400" height="600" fill="url(#g)"/><text x="200" y="280" text-anchor="middle" font-family="system-ui,sans-serif" font-size="48" fill="#6366f1">📅</text><text x="200" y="330" text-anchor="middle" font-family="system-ui,sans-serif" font-size="14" fill="#818cf8">Image unavailable</text></svg>');
  if (target.src !== fallback) {
    target.src = fallback;
    target.style.objectFit = 'cover';
  }
};
const EventCard: React.FC<EventCardProps> = ({
  title,
  date,
  time,
  type,
  description,
  image,
  location,
  link = '#',
  eventId,
  isSaved = false,
  onToggleSave,
  price,
  children,
  status,
  isVirtual,
  dateRange,
  ctaLabel,
  ctaDisabled,
  country
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  // Extract day and month for the date block
  const dateParts = date.split(' ');
  const month = dateParts[0]?.substring(0, 3) || '';
  const day = dateParts[1]?.replace(',', '') || '';
  return <div className={`rounded-xl shadow-sm overflow-hidden group hover:shadow-md transition-shadow border flex flex-col h-full ${dk.card}`} data-id="element-1529" data-mp="2kzmv2wvq6ayb">
      <div className="h-48 relative" data-id="element-1530" data-mp="2byp2daqqlpv1">
        <img src={image} alt={title} className="w-full h-full object-cover" onError={handleImgError} data-id="element-1531" data-mp="2bxne24kpn6vd" />
        <div className="absolute inset-0 bg-black/40" data-id="element-1532" data-mp="221alxb4td12q"></div>

        {/* Date Block — z-10 so badges can layer over it */}
        <div className={`absolute top-0 left-0 bottom-0 w-20 z-10 flex flex-col items-center justify-center text-white backdrop-blur-sm border-r border-white/10 ${isAfterTide ? 'bg-[#1a1a2e]/80' : 'bg-[#312E81]/80'}`} data-id="element-1533" data-mp="590la4b04vdl">
          <span className="text-xs font-bold uppercase tracking-wider" data-id="element-1534" data-mp="8qg26cjguwdk">
            {month}
          </span>
          <span className="text-3xl font-black leading-none my-1" data-id="element-1535" data-mp="cq70d1bboes4">{day}</span>
        </div>

        {/* Event Type Label — top-left, overlaps date block */}
        <div className="absolute top-3 left-3 z-20" data-id="element-1536" data-mp="mh1l218x76bg">
          <span className={`inline-block px-2 py-1 rounded text-xs font-bold text-white shadow-sm ${isVirtual ? isAfterTide ? 'bg-[#c77dba]' : 'bg-indigo-500' : 'bg-emerald-600'}`} data-id="element-1537" data-mp="3lx7zds2p77ye">
            {type}
          </span>
        </div>

        {/* Top-right badges: Virtual + (Status OR Price) */}
        {(status || price || isVirtual) && <div className="absolute top-3 right-3 z-20 flex gap-2" data-id="element-1538" data-mp="3t6aeq8lu8mk">
            {isVirtual && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-purple-100 text-purple-800" data-id="element-1539" data-mp="1a75r0f9kzi5m">
                <MonitorIcon className="h-3 w-3 mr-1" data-id="element-1540" data-mp="1f8lba53xpkmy" />
                Virtual
              </span>}
            {status ? <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${status.classes}`} data-id="element-1541" data-mp="2j5dwptf8ibo7">
                {status.label}
              </span> : price ? <span className="inline-block bg-amber-400 text-amber-900 text-xs font-bold px-2 py-1 rounded shadow-sm" data-id="element-1542" data-mp="2s48z0uf5r1m8">
                {price}
              </span> : null}
          </div>}

        {/* Country Tag — bottom-right */}
        {country && <div className="absolute bottom-3 right-3 z-20" data-id="element-1543" data-mp="3fveev9l3ltwj">
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-white/90 text-gray-800 shadow-sm backdrop-blur-sm" data-id="element-1544" data-mp="3fwz9ez7excue">
              <GlobeIcon className="h-3 w-3 mr-1" data-id="element-1545" data-mp="3ps9ejvt3y0ce" />
              {country}
            </span>
          </div>}
      </div>

      <div className="p-5 flex-1 flex flex-col" data-id="element-1546" data-mp="3kviyh8haz4qz">
        {/* Row 1: Time and Save Button */}
        <div className="flex items-center justify-between mb-3" data-id="element-1547" data-mp="8bol7pm5rhwf">
          <div className="flex items-center text-xs font-medium text-amber-500" data-id="element-1548" data-mp="d9zw83o6vyg8">
            <ClockIcon className="h-3.5 w-3.5 mr-1" data-id="element-1549" data-mp="3uhli4esotsv0" />
            {time}
          </div>
          {eventId !== undefined && onToggleSave && <SaveEventButton eventId={eventId} isSaved={isSaved} onToggleSave={onToggleSave} data-id="element-1550" data-mp="39zakb2ul68v" />}
        </div>

        {/* Row 2: Title */}
        <h3 className={`font-bold text-lg mb-1 transition-colors line-clamp-1 ${dk.textPrimary} ${rt.textHover}`} data-id="element-1551" data-mp="3qgrwxt6mqs5t">
          {title}
        </h3>

        {/* Row 3: Location */}
        {location && <div className={`flex items-start text-xs mb-2 ${dk.textMuted}`} data-id="element-1552" data-mp="3go2nf3un3od4">
            <MapPinIcon className="h-3.5 w-3.5 mr-1 mt-0.5 flex-shrink-0" data-id="element-1553" data-mp="32rp8o2hhq12c" />
            <span className="line-clamp-1" data-id="element-1554" data-mp="36s932yaoiqks">{location}</span>
          </div>}

        {/* Row 4: Description */}
        <p className={`text-sm mb-4 line-clamp-2 ${dk.textSecondary}`} data-id="element-1555" data-mp="1da21hlte0xwh">
          {description}
        </p>

        {/* Row 5: Date range */}
        {dateRange && <div className={`flex items-center text-sm mb-3 ${dk.textMuted}`} data-id="element-1556" data-mp="13ixnopfg7j38">
            <CalendarIcon className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-1557" data-mp="yke24q4ro9be" />
            <span data-id="element-1558" data-mp="ym76tzvtkjgr">{dateRange}</span>
          </div>}

        {/* Row 6: Custom children content (attendees, prices, etc) */}
        {children && <div className="mb-4 space-y-2" data-id="element-1559" data-mp="1r8jlp9k33s9n">{children}</div>}

        {/* Row 7: View Details / CTA button */}
        <div className="mt-auto pt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-1560" data-mp="1m9f9sb5rzgfm">
          {ctaLabel ? ctaDisabled ? <span className="block w-full text-center px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-gray-200 text-gray-500 cursor-not-allowed" data-id="element-1561" data-mp="1i83ky7uq6z8h">
                {ctaLabel}
              </span> : <Link to={link} className={`block w-full text-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${rt.primaryButton}`} data-id="element-1562" data-mp="2igjrowqy9hh7">
                {ctaLabel}
              </Link> : <Link to={link} className={`inline-flex items-center font-medium text-sm w-full justify-center py-2 rounded-lg transition-colors ${isAfterTide ? 'bg-[#e2b04a] text-[#1a1a2e] hover:bg-[#d1a03a]' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50'}`} data-id="element-1563" data-mp="2rfx49yadem65">
              View Details <ArrowRightIcon className="h-3.5 w-3.5 ml-1.5" data-id="element-1564" data-mp="3a89165i98v8w" />
            </Link>}
        </div>
      </div>
    </div>;
};
export default EventCard;